<script setup lang="ts">
import type { SiteBlockData, SiteBlockSettings } from '../../../types/site'

/**
 * Third-party iframe from a closed provider allowlist (Calendly, Google Maps,
 * Typeform, …). The allowlist is enforced in three places that must agree:
 * this block's provider picker, the `embed.url` regex in
 * Zth\SiteBuilder\GenericBlocks::EMBED_URL_PATTERN, and each consuming app's
 * CSP `frame-src`.
 *
 * On the sandbox: `allow-same-origin` here grants the frame ITS OWN origin
 * (calendly.com, google.com), not ours, so it gains no access to this page.
 * That is different from an iframe pointing at a file on our own origin, where
 * the pair allow-scripts + allow-same-origin would let the frame escape its
 * own sandbox — which is why agency's `html_embed` block must keep omitting it.
 * Calendly, Maps and Spotify do not function without it.
 */
const props = defineProps<{ data: SiteBlockData, settings: SiteBlockSettings, editing?: boolean }>()

const height = computed(() => `${props.data.height ?? 600}px`)
</script>

<template>
  <div v-if="data.url">
    <SiteSectionHeading
      :eyebrow="data.eyebrow"
      :title="data.headline"
      class="mb-5"
    />
    <iframe
      :src="data.url"
      :title="data.headline || 'Embedded content'"
      :style="{ height }"
      class="w-full rounded-[var(--site-radius)] border border-[var(--site-border)]"
      loading="lazy"
      referrerpolicy="strict-origin-when-cross-origin"
      allowfullscreen
      sandbox="allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox allow-forms allow-presentation"
    />
    <p
      v-if="data.caption"
      class="mt-2 text-center text-sm text-[var(--site-muted)]"
    >
      {{ data.caption }}
    </p>
  </div>
  <!-- No embed URL yet: silent on the public page (as before), a click
       target while building — see BlockFrame's `editing` doc comment. -->
  <div
    v-else-if="editing"
    class="flex flex-col items-center gap-2 rounded-[var(--site-radius)] border border-dashed border-[var(--site-border)] p-10 text-center text-sm text-[var(--site-muted)]"
  >
    <svg class="size-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true">
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <path d="M8 12h8M12 8v8" />
    </svg>
    <span>Pick a provider and paste its embed URL on the right.</span>
  </div>
</template>
